import { Outlet, NavLink, useLocation, useParams, useNavigate } from 'react-router-dom'
import { useState, useEffect, useRef, useMemo } from 'react'
import { useAuthStore } from '../stores/authStore'
import { useTheme } from '../hooks/useTheme'
import { api } from '../utils/api'
import { CommandPalette } from './CommandPalette'
import { Breadcrumbs } from './ui/Breadcrumb'
import {
  LayoutDashboard,
  Network,
  NetworkIcon,
  Box,
  Zap,
  PlugZap,
  Activity,
  CheckSquare,
  Play,
  Settings,
  FileText,
  LogOut,
  User,
  Search,
  Sun,
  Moon,
  Database,
  Cpu,
  ChevronDown,
  Layers,
  Shield,
  Terminal,
  Globe,
  Clock,
  Code,
  Calculator,
  LayoutGrid,
  Menu,
  X,
} from 'lucide-react'

import { JiraSyncBadge } from './integrations/JiraSyncBadge'
import { useWorkspace } from '../contexts/WorkspaceContext'
import { headerWorkspaceOptions, headerSelectValue, showSandboxBadge, PRODUCTION_WORKSPACE } from '../contexts/workspace.logic'
import AiWorkOrderModal from './integrations/AiWorkOrderModal'
import { useTelemetryStore } from '../stores/telemetryStore'

/* ------------------------------------------------------------------ */
/*  DC Selector                                                        */
/* ------------------------------------------------------------------ */

import { useDcStore } from '../stores/dcStore'
import { resolvePageDcCode, pathWithDcCode, DC_RESERVED_SEGMENTS } from '../utils/dcContext.logic'

function useDatacenters() {
  const { datacenters, fetchDatacenters, loading } = useDcStore()
  const hasFetched = useRef(false)

  useEffect(() => {
    if (datacenters.length === 0 && !loading && !hasFetched.current) {
      hasFetched.current = true
      fetchDatacenters()
    }
  }, [datacenters.length, fetchDatacenters, loading])

  return { 
    items: datacenters, 
    loading 
  }
}

/* ------------------------------------------------------------------ */
/*  Nav groups with lucide icons                                       */
/* ------------------------------------------------------------------ */

interface NavItem {
  path: string
  label: string
  icon: React.ComponentType<{ className?: string }>
}

interface NavGroup {
  label: string
  items: NavItem[]
  icon?: React.ComponentType<{ className?: string }>
}

const navGroups: NavGroup[] = [
  {
    label: 'ПРОСТРАНСТВО',
    icon: Globe,
    items: [
      { path: '/', label: 'Сводка', icon: LayoutDashboard },
      { path: '/floor-plan', label: 'План зала', icon: LayoutGrid },
    ],
  },
  {
    label: 'ИНФРАСТРУКТУРА',
    icon: Box,
    items: [
      { path: '/racks', label: 'Стойки', icon: Layers },
      { path: '/equipment', label: 'Оборудование', icon: Cpu },
      { path: '/placement', label: 'Размещение', icon: Layers },
    ],
  },
  {
    label: 'КОММУТАЦИЯ',
    icon: PlugZap,
    items: [
      { path: '/cables', label: 'Кабельный журнал', icon: NetworkIcon },
      { path: '/topology', label: 'Топология', icon: Network },
      { path: '/calculator', label: 'Калькулятор кабелей', icon: Calculator },
    ],
  },
  {
    label: 'ЭНЕРГЕТИКА',
    icon: Zap,
    items: [
      { path: '/power', label: 'Распределение фаз', icon: Activity },
    ],
  },
  {
    label: 'ИНТЕГРАЦИИ & НАРЯДЫ',
    icon: CheckSquare,
    items: [
      { path: '/discovery', label: 'Автообнаружение', icon: Code },
      { path: '/netbox', label: 'NetBox', icon: Database },
      { path: '/monitoring', label: 'Мониторинг', icon: Activity },
      { path: '/tasks', label: 'Наряды', icon: Clock },
      { path: '/scenarios', label: 'Сценарии', icon: Play },
    ],
  },
]

const adminGroup: NavGroup = {
  label: 'АДМИНИСТРИРОВАНИЕ',
  icon: Shield,
  items: [
    { path: '/admin', label: 'Панель', icon: Settings },
    { path: '/admin/audit', label: 'Аудит', icon: FileText },
    { path: '/admin/settings', label: 'Настройки', icon: Terminal },
  ],
}

/* ------------------------------------------------------------------ */
/*  Breadcrumb generator                                               */
/* ------------------------------------------------------------------ */

const pathLabels: Record<string, string> = {
  '/': 'Сводка',
  '/topology': 'Топология',
  '/floor-plan': 'План зала',
  '/edit': 'План зала',
  '/calculator': 'Калькулятор кабелей',
  '/cables': 'Кабельный журнал',
  '/racks': 'Стойки',
  '/equipment': 'Оборудование',
  '/power': 'Распределение фаз',
  '/placement': 'Размещение',
  '/netbox': 'NetBox',
  '/monitoring': 'Мониторинг',
  '/analytics': 'Аналитика',
  '/tasks': 'Наряды',
  '/scenarios': 'Сценарии',
  '/discovery': 'Автообнаружение',
  '/admin': 'Администрирование',
  '/admin/audit': 'Аудит',
  '/admin/settings': 'Настройки',
}

function useBreadcrumbs(dcCode: string | undefined, datacenters: any[]) {
  const location = useLocation()

  return useMemo(() => {
    const segments = location.pathname.split('/').filter(Boolean)
    const items: { label: string; href?: string }[] = [{ label: 'DC Cable Link', href: '/' }]

    let current = ''
    for (let i = 0; i < segments.length; i++) {
      const seg = segments[i]
      current += `/${seg}`
      let label = pathLabels[current] || seg.replace(/_/g, ' ')
      
      if (i === 0 && dcCode && seg === dcCode) {
        const dc = datacenters.find(d => d.code === dcCode)
        label = dc ? (dc.name || dc.code) : seg
      } else if (dcCode) {
        const pathWithoutDc = current.replace(`/${dcCode}`, '') || '/'
        label = pathLabels[pathWithoutDc] || label
      }

      items.push({ label, href: current })
    }

    return items
  }, [location.pathname, dcCode, datacenters])
}

/* ------------------------------------------------------------------ */
/*  Main component                                                     */
/* ------------------------------------------------------------------ */

export default function AppLayoutV2() {
  const { userRole, logout, username } = useAuthStore()
  const { activeWorkspaceId, setActiveWorkspaceId, isDraftMode } = useWorkspace()
  const { theme, toggle: toggleTheme } = useTheme()
  const [collapsed, setCollapsed] = useState(() => typeof window !== 'undefined' && window.innerWidth < 768)
  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const [showPalette, setShowPalette] = useState(false)
  
  const { activeDcCode, setActiveDcCode } = useDcStore()
  const telemetryStatus = useTelemetryStore(s => s.status)
  const { dcCode } = useParams<{ dcCode?: string }>()
  const navigate = useNavigate()
  const { items: dcs, loading: dcLoading } = useDatacenters()
  
  const currentDcCode = resolvePageDcCode(dcCode, activeDcCode, dcs[0]?.code)
  const userMenuRef = useRef<HTMLDivElement>(null)
  const breadcrumbs = useBreadcrumbs(dcCode, dcs)
  const location = useLocation()

  useEffect(() => {
    if (dcCode && !DC_RESERVED_SEGMENTS.includes(dcCode)) {
      setActiveDcCode(dcCode)
    }
  }, [dcCode, setActiveDcCode])



  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        setShowPalette(p => !p)
      }
      if (e.key === 'Escape') setShowPalette(false)
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [])

  const handleLogout = async () => {
    try { await api.post('/api/auth/logout') } catch { /* ignore */ }
    logout()
    window.location.href = '/login'
  }

  const [isAiModalOpen, setIsAiModalOpen] = useState(false)

  const allNavItems = [
    ...navGroups.flatMap(g => g.items),
    ...(userRole === 'admin' ? adminGroup.items : []),
    {
      path: '#ai-tasks',
      label: 'Создать задачи через AI',
      icon: CheckSquare,
      shortcut: 'AI',
      action: () => setIsAiModalOpen(true)
    }
  ]

  return (
    <div className="flex h-screen bg-bg-app text-text-primary overflow-hidden relative">
      {/* Mobile backdrop */}
      {!collapsed && (
        <div
          className="fixed inset-0 bg-black/60 z-40 md:hidden backdrop-blur-xs transition-opacity"
          onClick={() => setCollapsed(true)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`flex flex-col border-r border-border transition-all duration-200 z-50 bg-bg-app ${
          collapsed
            ? 'w-0 md:w-16 overflow-hidden border-r-0 md:border-r'
            : 'fixed inset-y-0 left-0 w-64 md:static md:w-60 shadow-xl md:shadow-none'
        }`}
      >
        {/* Logo */}
        <div className="flex items-center justify-between h-14 px-3 border-b border-border shrink-0">
          <button
            onClick={() => setCollapsed(c => !c)}
            className="flex items-center gap-2 px-2 py-1 rounded-sm hover:bg-bg-hover transition-colors flex-1 min-w-0"
            title={collapsed ? 'Раскрыть' : 'Свернуть'}
          >
            <span className="text-accent font-bold text-sm">DC</span>
            {!collapsed && (
              <span className="font-mono text-xs opacity-60 truncate">CABLE LINK</span>
            )}
          </button>
          {!collapsed && (
            <button
              onClick={() => setCollapsed(true)}
              className="md:hidden p-1 text-text-muted hover:text-text-primary"
              title="Закрыть"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Nav groups */}
        <nav className="flex-1 overflow-y-auto py-3 px-1.5">
          {navGroups.map(group => {
            const Icon = group.icon
            return (
              <div key={group.label} className="mb-4">
                {!collapsed && (
                  <div className="px-2 mb-1.5 flex items-center gap-1.5">
                    {Icon && <Icon className="w-3 h-3 text-text-muted" />}
                    <span className="text-[10px] uppercase tracking-widest text-text-muted font-medium">
                      {group.label}
                    </span>
                  </div>
                )}
                {group.items.map(item => {
                  const ItemIcon = item.icon
                  return (
                    <NavLink
                      key={item.path}
                      to={currentDcCode ? `/${currentDcCode}${item.path === '/' ? '' : item.path}` : item.path}
                      onClick={() => {
                        if (typeof window !== 'undefined' && window.innerWidth < 768) {
                          setCollapsed(true)
                        }
                      }}
                      className={({ isActive }) =>
                        `flex items-center gap-2.5 px-2 py-1.5 text-sm rounded-sm transition-colors ${
                          isActive
                            ? 'bg-accent text-white'
                            : 'text-text-secondary hover:text-text-primary hover:bg-bg-hover'
                        }`
                      }
                    >
                      <span className="shrink-0"><ItemIcon className="w-4 h-4" /></span>
                      {!collapsed && <span className="truncate">{item.label}</span>}
                    </NavLink>
                  )
                })}
              </div>
            )
          })}

          {userRole === 'admin' && (
            <div className="mt-4 pt-3 border-t border-border">
              {adminGroup.items.map(item => {
                const ItemIcon = item.icon
                return (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    onClick={() => {
                      if (typeof window !== 'undefined' && window.innerWidth < 768) {
                        setCollapsed(true)
                      }
                    }}
                    className={({ isActive }) =>
                      `flex items-center gap-2.5 px-2 py-1.5 text-sm rounded-sm transition-colors ${
                        isActive
                          ? 'bg-status-critical text-white'
                          : 'text-text-secondary hover:text-text-primary hover:bg-bg-hover'
                      }`
                    }
                  >
                    <span className="shrink-0"><ItemIcon className="w-4 h-4" /></span>
                    {!collapsed && <span className="truncate">{item.label}</span>}
                  </NavLink>
                )
              })}
            </div>
          )}
        </nav>
      </aside>

      {/* Main area */}
      <div className="flex flex-col flex-1 min-w-0">
        {/* Header */}
        <header className="h-14 border-b border-border flex items-center justify-between px-3 md:px-4 shrink-0 bg-bg-panel">
          {/* Mobile menu toggle */}
          <button
            onClick={() => setCollapsed(c => !c)}
            className="md:hidden mr-2 p-1.5 border border-border rounded-sm hover:bg-bg-hover text-text-secondary shrink-0"
            title="Меню"
          >
            <Menu className="w-4 h-4" />
          </button>

          {/* Breadcrumbs */}
          <div className="flex-1 min-w-0">
            <Breadcrumbs items={breadcrumbs} />
          </div>

            <div className="flex items-center gap-2 ml-4">
              <JiraSyncBadge />
              
              {/* Workspace: Production select; WO sandbox = badge + Reset */}
              <div className="flex items-center gap-2">
                {showSandboxBadge(activeWorkspaceId) && (
                  <>
                    <span
                      className="text-xs font-mono font-bold px-2 py-1 rounded-sm border border-status-warning bg-status-warning/10 text-status-warning max-w-40 truncate"
                      title={activeWorkspaceId}
                    >
                      {activeWorkspaceId}
                    </span>
                    <button
                      type="button"
                      onClick={() => setActiveWorkspaceId(PRODUCTION_WORKSPACE)}
                      className="text-xs px-2 py-1 rounded-sm border border-border text-text-secondary hover:border-accent hover:text-text-primary"
                    >
                      Сброс
                    </button>
                  </>
                )}
                <select
                  value={headerSelectValue(activeWorkspaceId)}
                  onChange={e => setActiveWorkspaceId(e.target.value)}
                  className={`text-xs border rounded-sm px-2 py-1 min-w-32 focus:border-accent outline-none font-mono ${
                    isDraftMode 
                      ? 'bg-status-warning/10 border-status-warning text-status-warning font-bold' 
                      : 'bg-bg-input border-border text-text-secondary'
                  }`}
                >
                  {headerWorkspaceOptions().map(o => (
                    <option key={o.id} value={o.id}>{o.label}</option>
                  ))}
                </select>
              </div>

            {/* DC selector */}
            <div className="flex items-center gap-2">
              {dcLoading ? (
                <span className="text-xs text-text-muted">ЦОД...</span>
              ) : (
                <select
                  value={currentDcCode}
                  onChange={e => {
                    const newDcCode = e.target.value
                    if (newDcCode) {
                      setActiveDcCode(newDcCode)
                      navigate(pathWithDcCode(location.pathname, newDcCode, currentDcCode))
                    }
                  }}
                  className="text-xs bg-bg-input border border-border rounded-sm px-2 py-1 min-w-28 focus:border-accent outline-none text-text-secondary"
                >
                  {dcs.map(dc => (
                    <option key={dc.id} value={dc.code}>
                      {dc.name || dc.code}
                    </option>
                  ))}
                </select>
              )}
            </div>

            {/* Telemetry Status Indicator */}
            <div
              className="flex items-center gap-1.5 px-2 py-1 border border-border rounded-sm text-[11px] font-mono text-text-secondary"
              title={
                telemetryStatus === 'connected' ? 'Телеметрия активна' :
                telemetryStatus === 'connecting' ? 'Подключение телеметрии...' :
                telemetryStatus === 'offline' ? 'Сеть недоступна' : 'Телеметрия приостановлена'
              }
            >
              <span className="relative flex h-2 w-2">
                {telemetryStatus === 'connecting' && (
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span>
                )}
                <span className={`relative inline-flex rounded-full h-2 w-2 ${
                  telemetryStatus === 'connected' ? 'bg-emerald-500' :
                  telemetryStatus === 'connecting' ? 'bg-amber-500' :
                  telemetryStatus === 'offline' ? 'bg-rose-500' : 'bg-slate-500'
                }`}></span>
              </span>
              <span className="hidden sm:inline opacity-80 select-none">
                {telemetryStatus === 'connected' ? 'live' :
                 telemetryStatus === 'connecting' ? 'sync' :
                 telemetryStatus === 'offline' ? 'offline' : 'paused'}
              </span>
            </div>

            {/* Search */}
            <button
              onClick={() => setShowPalette(true)}
              className="p-2 border border-border rounded-sm hover:bg-bg-hover transition-colors text-text-muted"
              title="Command Palette (⌘K)"
            >
              <Search className="w-4 h-4" />
            </button>

            {/* Theme */}
            <button
              onClick={toggleTheme}
              className="p-2 border border-border rounded-sm hover:bg-bg-hover transition-colors text-text-muted"
              title="Переключить тему"
            >
              {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </button>

            {/* User menu */}
            <div className="relative" ref={userMenuRef}>
              <button
                id="btn-user-profile"
                onClick={() => setUserMenuOpen(o => !o)}
                className="flex items-center gap-2 px-3 py-1.5 rounded-sm hover:bg-bg-hover transition-colors text-text-secondary"
              >
                <User className="w-4 h-4" />
                {!collapsed && (
                  <>
                    <span className="text-xs">{username || '—'}</span>
                    <ChevronDown className="w-3 h-3" />
                  </>
                )}
              </button>
              {userMenuOpen && (
                <div id="user-dropdown-menu" className="absolute right-0 top-full mt-1 w-56 bg-bg-panel border border-border rounded-sm shadow-lg z-50">
                  <div className="px-4 py-2.5 border-b border-border">
                    <p className="text-sm text-text-primary font-medium">{username || '—'}</p>
                    <p id="user-role-badge" className="text-xs text-text-muted">{userRole}</p>
                  </div>
                  <button
                    id="btn-logout"
                    onClick={handleLogout}
                    className="w-full text-left px-4 py-2.5 text-sm text-text-secondary hover:text-status-critical hover:bg-bg-hover transition-colors flex items-center gap-2"
                  >
                    <LogOut className="w-4 h-4" />
                    Выйти
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-auto">
          <Outlet />
        </main>
      </div>

      {/* Command Palette */}
      {showPalette && (
        <CommandPalette
          onClose={() => setShowPalette(false)}
          items={allNavItems.map(item => ({
            ...item,
            path: currentDcCode && item.path && !item.path.startsWith('/admin') && !item.path.startsWith('#')
              ? `/${currentDcCode}${item.path === '/' ? '' : item.path}`
              : item.path
          }))}
        />
      )}
      
      {/* AI Modal */}
      <AiWorkOrderModal 
        isOpen={isAiModalOpen} 
        onClose={() => setIsAiModalOpen(false)} 
        dcCode={currentDcCode}
        onCreated={() => setIsAiModalOpen(false)}
      />
    </div>
  )
}
